import { Theme } from "@mui/material/styles";

/**
 * Grey Flat UI color system for Portfolio Dashboard
 * Following institutional trading platform aesthetics (Bloomberg/Reuters)
 * No gradients, no shadows, functional colors only - Grey theme
 */
export const getFlatPortfolioColors = (theme: Theme) => ({
  // Background colors - grey hierarchy with subtle differences
  background: {
    primary: theme.palette.mode === "dark" 
      ? "#1a1a1a"    // Dark grey (main background)
      : "#ffffff",   // Pure white
    secondary: theme.palette.mode === "dark" 
      ? "#2a2a2a"    // Medium grey (cards/sections)
      : "#f8f9fa",   // Very light gray
    tertiary: theme.palette.mode === "dark"
      ? "#3a3a3a"    // Lighter grey (hover states)
      : "#f1f5f9",   // Light gray
    hover: theme.palette.mode === "dark" 
      ? "rgba(255, 255, 255, 0.02)" 
      : "rgba(0, 0, 0, 0.01)",
    active: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.02)",
  },
  
  // Border colors - clear separation without depth
  border: {
    primary: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"    // 10% white
      : "rgba(0, 0, 0, 0.1)",          // 10% black
    secondary: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"   // 5% white (subtle)
      : "rgba(0, 0, 0, 0.05)",         // 5% black (subtle)
    strong: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.2)"    // 20% white (emphasis)
      : "rgba(0, 0, 0, 0.2)",          // 20% black (emphasis)
  },
  
  // Text colors - clear hierarchy
  text: {
    primary: theme.palette.mode === "dark" 
      ? "#ffffff"      // Pure white
      : "#1d1d1f",     // Near black
    secondary: theme.palette.mode === "dark" 
      ? "#9ca3af"      // Gray-400
      : "#6b7280",     // Gray-500
    muted: theme.palette.mode === "dark" 
      ? "#6b7280"      // Gray-500
      : "#9ca3af",     // Gray-400
    disabled: theme.palette.mode === "dark"
      ? "#4b5563"      // Gray-600
      : "#d1d5db",     // Gray-300
  },
  
  // Functional colors - consistent across themes
  functional: {
    // Success/Gains - Green
    success: "#22c55e",        // Primary green
    successDark: "#16a34a",    // Darker green (hover)
    successLight: "#4ade80",   // Lighter green
    successBg: theme.palette.mode === "dark"
      ? "rgba(34, 197, 94, 0.1)"
      : "rgba(34, 197, 94, 0.05)",
    
    // Error/Losses - Red
    error: "#ef4444",          // Primary red
    errorDark: "#dc2626",      // Darker red (hover)
    errorLight: "#f87171",     // Lighter red
    errorBg: theme.palette.mode === "dark"
      ? "rgba(239, 68, 68, 0.1)"
      : "rgba(239, 68, 68, 0.05)",
    
    // Warning - Amber
    warning: "#f59e0b",        // Primary amber
    warningDark: "#d97706",    // Darker amber
    warningLight: "#fbbf24",   // Lighter amber
    warningBg: theme.palette.mode === "dark"
      ? "rgba(245, 158, 11, 0.1)"
      : "rgba(245, 158, 11, 0.05)",
    
    // Info - Blue
    info: "#3b82f6",           // Primary blue
    infoDark: "#2563eb",       // Darker blue
    infoLight: "#60a5fa",      // Lighter blue
    infoBg: theme.palette.mode === "dark"
      ? "rgba(59, 130, 246, 0.1)"
      : "rgba(59, 130, 246, 0.05)",
  },
  
  // Chart colors - for data visualization
  chart: {
    primary: "#22c55e",
    secondary: "#3b82f6",
    tertiary: "#f59e0b",
    quaternary: "#8b5cf6",
    grid: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)",
    axis: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.2)"
      : "rgba(0, 0, 0, 0.2)",
  },
});

// Typography styles for Portfolio Dashboard
export const portfolioTypography = {
  // Hero value (e.g., total portfolio value)
  hero: {
    fontSize: { xs: "2rem", md: "2.5rem" },
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "-0.02em",
  },
  
  // Section titles
  title: {
    fontSize: "1.125rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  
  // Metric labels (uppercase, small)
  label: {
    fontSize: "0.75rem",
    fontWeight: 500,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
  },
  
  // Metric values
  value: {
    fontSize: "1.25rem",
    fontWeight: 600,
  },
  
  // Table headers
  tableHeader: {
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
  },
  
  // Body text
  body: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.5,
  },
  
  // Small text
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 1.4,
  },
};

// Spacing system based on 8px grid
export const portfolioSpacing = {
  xs: 0.5,  // 4px
  sm: 1,    // 8px
  md: 2,    // 16px
  lg: 3,    // 24px
  xl: 4,    // 32px
  xxl: 6,   // 48px
};